/**
 * RankedListCard — reusable top-N list for Drones / Serials / Protocols.
 *
 * Dedrone shows these as a ranked horizontal bar inside a card with the
 * full count on the right. We match that pattern because it's scannable and
 * doesn't need a legend.
 *
 * Rows come pre-sorted from the server; this component only handles
 * rendering + a "show all" toggle if the list is longer than `initialLimit`.
 */
import { useMemo, useState } from 'react';
import clsx from 'clsx';

export interface RankedRow {
  label: string;
  sublabel?: string;
  count: number;
  // Distinct days the row was seen — controls "repeat offender" coloring
  // (>= REPEAT_THRESHOLD_DAYS). Undefined ⇒ feature off for this row.
  repeatCount?: number;
}

// Days-distinct threshold above which a row is highlighted in red. Three
// distinct days felt right in early testing — one-off events stay neutral,
// recurring activity stands out without crying wolf for two-day shows.
const REPEAT_THRESHOLD_DAYS = 3;

interface Props {
  title: string;
  rows: RankedRow[];
  icon?: React.ComponentType<{ className?: string }>;
  emptyText?: string;
  initialLimit?: number;
  barColor?: string;
}

export default function RankedListCard({
  title,
  rows,
  icon: Icon,
  emptyText = 'No data in range',
  initialLimit = 5,
  barColor = 'bg-accent/70',
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const max = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.count), 0),
    [rows],
  );

  const visible = expanded ? rows : rows.slice(0, initialLimit);

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-accent" />}
          <h3 className="text-sm font-semibold text-white">{title}</h3>
        </div>
        <span className="text-xs text-gray-500">{rows.length} total</span>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">{emptyText}</p>
      ) : (
        <>
          <ul className="space-y-2">
            {visible.map((r, idx) => {
              const pct = max > 0 ? (r.count / max) * 100 : 0;
              const isRepeat =
                r.repeatCount !== undefined && r.repeatCount >= REPEAT_THRESHOLD_DAYS;
              return (
                <li key={`${r.label}-${idx}`} className="space-y-1">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-white flex items-center gap-1.5">
                        <span className="truncate">{r.label}</span>
                        {isRepeat && (
                          <span
                            className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-500/15 text-rose-300 border border-rose-500/30"
                            title={`Seen across ${r.repeatCount} distinct days`}
                          >
                            ↻ {r.repeatCount}d
                          </span>
                        )}
                      </div>
                      {r.sublabel && (
                        <div className="truncate text-xs text-gray-500">
                          {r.sublabel}
                        </div>
                      )}
                    </div>
                    <span className="font-mono text-gray-300 flex-shrink-0">
                      {r.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-navy-700 rounded-full overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full',
                        // Repeat offenders override the per-card barColor —
                        // they're more important to spot than the card's
                        // categorical theme.
                        isRepeat ? 'bg-rose-400/80' : barColor,
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>

          {rows.length > initialLimit && (
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-3 text-xs text-accent hover:text-accent-light"
            >
              {expanded ? 'Show less' : `Show all ${rows.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
